import { useRef, useState } from 'react'
import {
  rotateRecoveryKeyFlow,
  RecoveryKeyRotateError,
  type RecoveryKeyFlowDeps,
} from '../services/recoveryKeyRotateFlow'

/**
 * `pages/settings/RotateRecoveryKeySection.tsx` 用 state machine + trigger (Track E-2)。
 *
 * 状態遷移:
 *   idle → submitting → revealed(mnemonic) | error
 *   error → submitting → ... (再試行可能)
 *
 * Mnemonic は `revealed` 状態の中でのみ保持され、`reset()` で破棄される
 * (`closing` 時に caller が必ず呼ぶこと)。
 */

export type RotateRecoveryKeyState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'revealed'; mnemonic: string }
  | { kind: 'error'; messageKey: string }

const MESSAGE_KEYS: Record<RecoveryKeyRotateError['code'], string> = {
  no_session: 'settings.security.rotate_recovery_key_error.no_session',
  too_many_requests: 'settings.security.rotate_recovery_key_error.too_many_requests',
  network: 'settings.security.rotate_recovery_key_error.network',
  generic: 'settings.security.rotate_recovery_key_error.generic',
}

const messageKeyFor = (err: unknown): string => {
  if (err instanceof RecoveryKeyRotateError) return MESSAGE_KEYS[err.code]

  return MESSAGE_KEYS.generic
}

export interface UseRotateRecoveryKeyForm {
  state: RotateRecoveryKeyState
  isSubmitting: boolean
  /** ユーザがトリガしたとき呼ぶ (button onClick) */
  start: () => Promise<void>
  /** mnemonic 表示を閉じる前に呼ぶ — state を `idle` に戻し、mnemonic への参照を切る */
  reset: () => void
}

export interface UseRotateRecoveryKeyFormOptions {
  deps?: RecoveryKeyFlowDeps
}

export const useRotateRecoveryKeyForm = (
  options: UseRotateRecoveryKeyFormOptions = {},
): UseRotateRecoveryKeyForm => {
  const [state, setState] = useState<RotateRecoveryKeyState>({ kind: 'idle' })
  const submittingRef = useRef(false)

  const start = async (): Promise<void> => {
    if (submittingRef.current) return

    submittingRef.current = true
    setState({ kind: 'submitting' })

    try {
      const result = await rotateRecoveryKeyFlow(options.deps)

      setState({ kind: 'revealed', mnemonic: result.mnemonic })
    } catch (err: unknown) {
      setState({ kind: 'error', messageKey: messageKeyFor(err) })
    } finally {
      submittingRef.current = false
    }
  }

  const reset = (): void => {
    setState({ kind: 'idle' })
  }

  return {
    state,
    isSubmitting: state.kind === 'submitting',
    start,
    reset,
  }
}
